import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Box, Typography, useTheme } from '@mui/material'

import { centredDialogActionsSx } from '../../helpers/styles'
import { useMotivationDiagnosisDetail } from '../../hooks/useMotivationDiagnosisDetail'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { HeroAndTableSkeleton } from '../HeroAndTableSkeleton/HeroAndTableSkeleton'
import { MotivationPatternGuide } from '../MotivationPatternGuide/MotivationPatternGuide'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'
import { UAInsightsDialogActions } from '../UAInsightsDialogActions/UAInsightsDialogActions'

/**
 * Drilldown for a single Blind Spot card on the "What's working, what's not?"
 * section of the UA Insights game page. Guides the user to carry the
 * blind-spot motivation with the right narrative pattern: the recommended
 * pattern mix across the genre's motivation-leaning ads, and how the
 * motivation maps onto that pattern's beats.
 */
export const BlindSpotDetailsDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack } = useUAInsightsNavigation()
  const { motivationLabel, genreLabel } = useUATaxonomy()
  const { gameId: gameIdParam } = useParams<{ gameId: string }>()
  const sourceGameId = gameIdParam ?? null

  const open = params.dialog === 'blind-spot'
  const motivationId = open ? params.motivationId : null
  const { data, isLoading, isError, refetch } = useMotivationDiagnosisDetail(motivationId, sourceGameId)
  const { motivationById } = useUAInsightsMeta()

  const theme = useTheme()
  const motivation = data?.motivation ?? (motivationId ? motivationById(motivationId) : undefined)
  const resolvedMotivation = motivation ? motivationLabel(motivation) : ''

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={data ? t('blind_spot_dialog_title', { motivation: resolvedMotivation, game: data.game.name }) : t('blind_spot_dialog_title_fallback')}
      canGoBack={canGoBack}
      onGoBack={goBack}
      showNetworkSelector
      maxWidth="lg"
      paperSx={null}
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      loadingPlaceholder={<HeroAndTableSkeleton sections={2} />}
      notFoundFallback={
        <Typography variant="body2" color="text.secondary">
          {t('blind_spot_not_found')}
        </Typography>
      }
      actions={<UAInsightsDialogActions disabled={!data} />}
      actionsSx={centredDialogActionsSx}
    >
      {data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {data.narrativePattern ? (
            <MotivationPatternGuide
              narrativePattern={data.narrativePattern}
              motivationLabel={resolvedMotivation}
              gameName={data.game.name}
              genre={genreLabel(data.game.genre)}
              accent={theme.palette.primary.main}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('blind_spot_pattern_unavailable', { motivation: resolvedMotivation })}
            </Typography>
          )}
        </Box>
      )}
    </UAInsightsDialog>
  )
}
